/**
 * GLOPE's own icon set: chunky, rounded, drawn on a 24×24 grid. The same path
 * data renders as inline SVG in the page and as Path2D on the game canvas, so
 * the HUD and the buttons always match.
 */
type Shape = string | { d: string; stroke?: number; evenodd?: boolean };

export const ICONS = {
  eye: [{ d: 'M12 5C6.6 5 2.8 9 1.5 12c1.3 3 5.1 7 10.5 7s9.2-4 10.5-7C21.2 9 17.4 5 12 5ZM12 8a4 4 0 1 0 0 8 4 4 0 1 0 0-8ZM12 10.2a1.8 1.8 0 1 0 0 3.6 1.8 1.8 0 1 0 0-3.6Z', evenodd: true }],
  person: ['M12 2.5a4.2 4.2 0 1 0 0 8.4 4.2 4.2 0 1 0 0-8.4Z', 'M3.8 20.5c0-4.6 3.7-8 8.2-8s8.2 3.4 8.2 8c0 1-.8 1.8-1.8 1.8H5.6c-1 0-1.8-.8-1.8-1.8Z'],
  trophy: [
    'M6.5 2.5h11V9a5.5 5.5 0 0 1-11 0V2.5Z',
    { d: 'M6.8 5H4.2v1.4A3.6 3.6 0 0 0 7.8 10M17.2 5h2.6v1.4a3.6 3.6 0 0 1-3.6 3.6', stroke: 2.2 },
    'M10.6 14h2.8v3.5h-2.8Z',
    'M7.5 17.5h9c.8 0 1.5.7 1.5 1.5v2.5H6V19c0-.8.7-1.5 1.5-1.5Z',
  ],
  speed: ['M14.2 1.5 4.3 13.4c-.4.5 0 1.1.6 1.1h5.6l-1.7 7.2c-.2.7.7 1.1 1.1.6l9.9-12c.4-.5 0-1.1-.6-1.1h-5.8l1.9-7.1c.2-.7-.7-1.1-1.1-.6Z'],
  rain: [
    'M6.8 13.5a4.6 4.6 0 0 1-.6-9.2 6.4 6.4 0 0 1 11.9 1.5 3.9 3.9 0 0 1-.4 7.7H6.8Z',
    'M6.3 15.5h3.4l-1.7 6Z',
    'M10.3 15.5h3.4L12 22.5Z',
    'M14.3 15.5h3.4l-1.7 6Z',
  ],
  dark: ['M20.6 14.6A9 9 0 1 1 9.4 3.4a7.2 7.2 0 0 0 11.2 11.2Z'],
  flip: [
    { d: 'M3.5 8h13M20.5 16h-13', stroke: 2.6 },
    'M15.5 3.8 21 8l-5.5 4.2Z',
    'M8.5 11.8 3 16l5.5 4.2Z',
  ],
  horns: [
    {
      d: 'M12 6.5a7.8 7.8 0 1 0 0 15.6 7.8 7.8 0 1 0 0-15.6ZM7.6 11.4l3.4 1.4-.4 1.7c-1.6.3-2.8-.8-3-3.1ZM16.4 11.4l-3.4 1.4.4 1.7c1.6.3 2.8-.8 3-3.1ZM8.4 16.6c2.3 1.6 4.9 1.6 7.2 0-.5 2.1-2 3.3-3.6 3.3s-3.1-1.2-3.6-3.3Z',
      evenodd: true,
    },
    'M3.6 1.8c-.8 3.6.4 6.4 3.3 7.8l1.9-2.1C6.6 6.6 4.9 4.8 3.6 1.8Z',
    'M20.4 1.8c.8 3.6-.4 6.4-3.3 7.8l-1.9-2.1c2.2-.9 3.9-2.7 5.2-5.7Z',
  ],
  skull: [{
    d: 'M12 2C7 2 3.3 5.4 3.3 10c0 2.7 1.2 4.7 3 5.9V19c0 .8.7 1.5 1.5 1.5h8.4c.8 0 1.5-.7 1.5-1.5v-3.1c1.8-1.2 3-3.2 3-5.9C20.7 5.4 17 2 12 2ZM8.4 9.2a2.3 2.3 0 1 0 0 4.6 2.3 2.3 0 1 0 0-4.6ZM15.6 9.2a2.3 2.3 0 1 0 0 4.6 2.3 2.3 0 1 0 0-4.6ZM12 13.8l1.3 2.2h-2.6ZM9.6 17.6h1.2v2.9H9.6ZM13.2 17.6h1.2v2.9h-1.2Z',
    evenodd: true,
  }],
  play: ['M7 4.2v15.6c0 .9 1 1.4 1.7.9l12-7.8c.7-.4.7-1.4 0-1.8l-12-7.8C8 2.8 7 3.3 7 4.2Z'],
  hourglass: [
    'M5.5 2h13v2H17v1.6c0 2.4-1.6 4.4-3.4 6.4 1.8 2 3.4 4 3.4 6.4V20h1.5v2h-13v-2H7v-1.6c0-2.4 1.6-4.4 3.4-6.4C8.6 10 7 8 7 5.6V4H5.5V2Z',
  ],
  left: ['M16.5 4.3v15.4c0 .9-1 1.4-1.7.9L4.6 12.9a1.1 1.1 0 0 1 0-1.8l10.2-7.7c.7-.5 1.7 0 1.7.9Z'],
  right: ['M7.5 4.3v15.4c0 .9 1 1.4 1.7.9l10.2-7.7a1.1 1.1 0 0 0 0-1.8L9.2 3.4c-.7-.5-1.7 0-1.7.9Z'],
  up: ['M11.1 2.9 3 12.3c-.6.7-.1 1.7.8 1.7H8v6.5c0 .8.7 1.5 1.5 1.5h5c.8 0 1.5-.7 1.5-1.5V14h4.2c.9 0 1.4-1 .8-1.7l-8.1-9.4a1.2 1.2 0 0 0-1.8 0Z'],
  send: ['M3.4 10.6 20.2 3.2c.7-.3 1.4.4 1.1 1.1l-7.4 16.8c-.3.8-1.5.7-1.7-.1l-1.7-6.1a1 1 0 0 0-.7-.7l-6.1-1.7c-.8-.2-.9-1.4-.1-1.7Z'],
  close: [{ d: 'M6 6l12 12M18 6 6 18', stroke: 3 }],
  sound: [
    'M2.5 9.6c0-.6.5-1.1 1.1-1.1h3.1l4.6-4c.7-.6 1.7-.1 1.7.8v13.4c0 .9-1 1.4-1.7.8l-4.6-4H3.6c-.6 0-1.1-.5-1.1-1.1V9.6Z',
    { d: 'M16 9a4.2 4.2 0 0 1 0 6M19 6a8.4 8.4 0 0 1 0 12', stroke: 2.2 },
  ],
  muted: [
    'M2.5 9.6c0-.6.5-1.1 1.1-1.1h3.1l4.6-4c.7-.6 1.7-.1 1.7.8v13.4c0 .9-1 1.4-1.7.8l-4.6-4H3.6c-.6 0-1.1-.5-1.1-1.1V9.6Z',
    { d: 'M16 9.5l5 5M21 9.5l-5 5', stroke: 2.4 },
  ],
  coin: [{ d: 'M12 3a9 9 0 1 0 0 18 9 9 0 1 0 0-18ZM10.5 7.5h3v9h-3Z', evenodd: true }],
  share: [
    'M18 2a3.2 3.2 0 1 0 0 6.4A3.2 3.2 0 1 0 18 2Z',
    'M6 8.8a3.2 3.2 0 1 0 0 6.4A3.2 3.2 0 1 0 6 8.8Z',
    'M18 15.6a3.2 3.2 0 1 0 0 6.4 3.2 3.2 0 1 0 0-6.4Z',
    { d: 'M8.7 10.6l6.6-3.8M8.7 13.4l6.6 3.8', stroke: 2.2 },
  ],
  again: [{ d: 'M19.6 13.5A7.8 7.8 0 1 1 16.4 5.6', stroke: 2.8 }, 'M14.3 2.6l6.4 1.3-2.3 6.2Z'],
  skip: ['M2.5 5.6v12.8c0 .9 1 1.4 1.7.9l8.3-6.4v5.5c0 .9 1 1.4 1.7.9l8.3-6.4c.6-.5.6-1.3 0-1.8l-8.3-6.4c-.7-.5-1.7 0-1.7.9v5.5L4.2 4.7c-.7-.5-1.7 0-1.7.9Z'],
  dot: ['M12 5.5a6.5 6.5 0 1 0 0 13 6.5 6.5 0 1 0 0-13Z'],
} satisfies Record<string, Shape[]>;

export type IconName = keyof typeof ICONS;

const SVG = 'http://www.w3.org/2000/svg';

/** An inline SVG icon, coloured by the surrounding text colour. */
export function iconSvg(name: IconName, cls = 'ic'): SVGSVGElement {
  const svg = document.createElementNS(SVG, 'svg');
  svg.setAttribute('viewBox', '0 0 24 24');
  svg.setAttribute('class', cls);
  svg.setAttribute('aria-hidden', 'true');
  for (const s of ICONS[name] as Shape[]) {
    const p = document.createElementNS(SVG, 'path');
    const { d, stroke, evenodd } = typeof s === 'string' ? { d: s } : s;
    p.setAttribute('d', d);
    if (stroke) {
      p.setAttribute('fill', 'none');
      p.setAttribute('stroke', 'currentColor');
      p.setAttribute('stroke-width', String(stroke));
      p.setAttribute('stroke-linecap', 'round');
      p.setAttribute('stroke-linejoin', 'round');
    } else {
      p.setAttribute('fill', 'currentColor');
      if (evenodd) p.setAttribute('fill-rule', 'evenodd');
    }
    svg.append(p);
  }
  return svg;
}

/** Swaps every `<i data-icon="name">` placeholder in the page for its SVG. */
export function hydrateIcons(root: ParentNode = document): void {
  for (const el of root.querySelectorAll<HTMLElement>('i[data-icon]')) {
    el.replaceWith(iconSvg(el.dataset.icon as IconName, `ic ${el.className}`.trim()));
  }
}

const paths = new Map<string, Path2D>();

/** Draws an icon on a canvas with its top-left corner at (x, y). */
export function drawIcon(ctx: CanvasRenderingContext2D, name: IconName, x: number, y: number, size: number, color: string): void {
  ctx.save();
  ctx.translate(x, y);
  ctx.scale(size / 24, size / 24);
  ctx.fillStyle = color;
  ctx.strokeStyle = color;
  ctx.lineCap = 'round';
  ctx.lineJoin = 'round';
  for (const s of ICONS[name] as Shape[]) {
    const { d, stroke, evenodd } = typeof s === 'string' ? { d: s } : s;
    let p = paths.get(d);
    if (!p) paths.set(d, (p = new Path2D(d)));
    if (stroke) {
      ctx.lineWidth = stroke;
      ctx.stroke(p);
    } else {
      ctx.fill(p, evenodd ? 'evenodd' : 'nonzero');
    }
  }
  ctx.restore();
}
